import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi.js';
import { useAuth } from '../context/AuthContext.jsx';

function getMondayOfCurrentWeek() {
  const d = new Date();
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

export default function DashboardPage() {
  const api = useApi();
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';

  const [stats, setStats] = useState(null);
  const [roster, setRoster] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);

  const weekStart = getMondayOfCurrentWeek();

  useEffect(() => {
    async function load() {
      try {
        const [rosterData] = await Promise.all([
          api.get(`/roster?week=${weekStart}`),
        ]);

        setRoster(rosterData.roster);
        if (rosterData.open_requests) {
          setPendingRequests(rosterData.open_requests.filter(r => r.status === 'pending'));
        }

        if (isOwner) {
          const [employees, openShifts] = await Promise.all([
            api.get('/employees'),
            api.get('/roster/open-shifts'),
          ]);

          setStats({
            employeeCount: employees.length,
            activeEmployees: employees.filter(e => e.is_active).length,
            shiftCount: rosterData.shifts?.length || 0,
            openShiftCount: openShifts.open_shifts?.length || 0,
            pendingRequestCount: openShifts.open_shifts?.reduce((n, s) => n, 0) || 0,
          });
        } else {
          // Staff view – show own shifts this week
          const myShifts = (rosterData.shifts || []).filter(
            s => s.employee_id === user.employee_id
          );
          setStats({
            myShiftsThisWeek: myShifts.length,
            myHoursThisWeek: myShifts.reduce((sum, s) => {
              const [sh, sm] = s.start_time.split(':').map(Number);
              const [eh, em] = s.end_time.split(':').map(Number);
              return sum + (eh * 60 + em - sh * 60 - sm) / 60;
            }, 0).toFixed(1),
          });
        }
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [weekStart]);

  if (loading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: 64 }}>
          <span className="spinner spinner-dark" style={{ width: 32, height: 32 }} />
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            Welcome back, {user?.name}
          </h1>
          <p className="page-subtitle">
            {isOwner ? `Managing ${user?.name}` : `${user?.employeeRole?.replace(/_/g, ' ')} team member`}
          </p>
        </div>
        {isOwner && (
          <Link to="/roster" className="btn btn-primary">
            View Roster
          </Link>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="stats-grid">
          {isOwner ? (
            <>
              <div className="stat-card">
                <div className="stat-label">Active Employees</div>
                <div className="stat-value">{stats.activeEmployees}</div>
                <div className="stat-sub">of {stats.employeeCount} total</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Shifts This Week</div>
                <div className="stat-value">{stats.shiftCount}</div>
                <div className="stat-sub">
                  {roster ? (
                    <span className={`badge badge-${roster.status === 'published' ? 'success' : roster.status === 'draft' ? 'warning' : 'secondary'}`}>
                      {roster.status}
                    </span>
                  ) : 'No roster generated'}
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Open Shifts</div>
                <div className="stat-value" style={{ color: stats.openShiftCount > 0 ? 'var(--color-danger)' : 'inherit' }}>
                  {stats.openShiftCount}
                </div>
                <div className="stat-sub">need to be filled</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Pending Requests</div>
                <div className="stat-value" style={{ color: pendingRequests.length > 0 ? 'var(--color-warning)' : 'inherit' }}>
                  {pendingRequests.length}
                </div>
                <div className="stat-sub">awaiting approval</div>
              </div>
            </>
          ) : (
            <>
              <div className="stat-card">
                <div className="stat-label">My Shifts This Week</div>
                <div className="stat-value">{stats.myShiftsThisWeek}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">My Hours This Week</div>
                <div className="stat-value">{stats.myHoursThisWeek}h</div>
              </div>
            </>
          )}
        </div>
      )}

      <div className="dashboard-grid">
        {/* Pending requests (owner) */}
        {isOwner && pendingRequests.length > 0 && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Pending Shift Requests</span>
              <Link to="/open-shifts" className="btn btn-secondary btn-sm">View All</Link>
            </div>
            <div className="card-body">
              {pendingRequests.slice(0, 5).map(req => (
                <div key={req.id} className="flex items-center justify-between" style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div>
                    <div className="font-semibold" style={{ fontSize: 13 }}>{req.requester_name}</div>
                    <div className="text-muted text-sm">Requested a shift</div>
                  </div>
                  <Link to="/open-shifts" className="btn btn-primary btn-sm">Review</Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick links */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Quick Actions</span>
          </div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Link to="/roster" className="btn btn-secondary" style={{ justifyContent: 'flex-start' }}>
              View This Week's Roster
            </Link>
            <Link to="/availability" className="btn btn-secondary" style={{ justifyContent: 'flex-start' }}>
              {isOwner ? 'View Staff Availability' : 'View My Availability'}
            </Link>
            <Link to="/open-shifts" className="btn btn-secondary" style={{ justifyContent: 'flex-start' }}>
              {isOwner ? 'Manage Open Shifts' : 'Browse Open Shifts'}
            </Link>
            {isOwner && (
              <>
                <Link to="/employees" className="btn btn-secondary" style={{ justifyContent: 'flex-start' }}>
                  Manage Employees
                </Link>
                <Link to="/settings" className="btn btn-secondary" style={{ justifyContent: 'flex-start' }}>
                  Staffing Rules & Settings
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Roster status card */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Current Week Roster</span>
            <Link to="/roster" className="btn btn-secondary btn-sm">Open</Link>
          </div>
          <div className="card-body">
            {roster ? (
              <div>
                <div style={{ marginBottom: 10 }}>
                  Status: <span className={`badge badge-${roster.status === 'published' ? 'success' : roster.status === 'draft' ? 'warning' : 'secondary'}`}>
                    {roster.status}
                  </span>
                </div>
                <div className="text-muted text-sm">
                  Week of {new Date(roster.week_start + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}
                </div>
                {roster.generated_at && (
                  <div className="text-muted text-sm mt-1">
                    Generated {new Date(roster.generated_at).toLocaleDateString('en-AU')}
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-state" style={{ padding: '20px 0' }}>
                <p>No roster for this week yet.</p>
                {isOwner && (
                  <Link to="/roster" className="btn btn-primary btn-sm">Generate Roster</Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
